import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { ApiError } from "@/api/client";
import { downloadContactExport, useContactEnrolments, useConversations, useEnrol, useEraseContact, useSequences, useStopEnrolment } from "@/api/engage";
import { useSaveContact } from "@/api/hooks";
import type { Contact } from "@/api/types";
import { Badge } from "@/components/crm/Badge";
import { Alert, Modal, formatDate } from "@/components/ui";
import { titleCase } from "@/lib/format";
import { can } from "@/lib/permissions";

/** Consent with its time and source: what GDPR, DPDP and WhatsApp's policy ask you to keep. */
export function ConsentPanel({ c, perms }: { c: Contact; perms: string[] }) {
  const save = useSaveContact(c.id);
  const [error, setError] = useState<string | null>(null);
  const editable = can(perms, "contacts:update");
  const toggle = (body: Record<string, unknown>, source: string) => {
    setError(null);
    save.mutateAsync({ ...body, consent_source: source }).catch((e) => setError(e instanceof ApiError ? e.message : "Could not save"));
  };
  return (
    <section className="card space-y-3 p-5" aria-label="Consent">
      <h2 className="font-semibold">Consent</h2>
      {error && <Alert>{error}</Alert>}
      <div className="flex items-start justify-between gap-3">
        <div><p className="text-sm font-medium">WhatsApp messages</p>
          <p className="text-xs text-muted" data-testid="wa-consent">{c.whatsapp_opt_in ? `Opted in ${c.whatsapp_opt_in_at ? formatDate(c.whatsapp_opt_in_at) : ""}` : c.whatsapp_opt_in_at ? `Opted out ${formatDate(c.whatsapp_opt_in_at)}` : "No opt-in recorded"}</p></div>
        {editable && <button type="button" className="btn-ghost px-2 py-1 text-xs" disabled={save.isPending}
          onClick={() => toggle({ whatsapp_opt_in: !c.whatsapp_opt_in }, c.whatsapp_opt_in ? "Withdrawn, recorded by staff" : "Given verbally, recorded by staff")}>
          {c.whatsapp_opt_in ? "Record opt-out" : "Record opt-in"}</button>}
      </div>
      <div className="flex items-start justify-between gap-3">
        <div><p className="text-sm font-medium">Follow-up and marketing email</p>
          <p className="text-xs text-muted" data-testid="email-consent">{c.email_opt_out ? `Unsubscribed ${c.email_opt_out_at ? formatDate(c.email_opt_out_at) : ""}` : "Subscribed"}</p></div>
        {editable && <button type="button" className="btn-ghost px-2 py-1 text-xs" disabled={save.isPending}
          onClick={() => toggle({ email_opt_out: !c.email_opt_out }, c.email_opt_out ? "Resubscribed on request" : "Unsubscribed on request")}>
          {c.email_opt_out ? "Resubscribe" : "Unsubscribe"}</button>}
      </div>
      {c.consent_source && <p className="text-xs text-muted">Last change: {c.consent_source}</p>}
      <p className="text-xs text-muted">Source: {titleCase(c.source)}</p>
    </section>
  );
}

export function ConversationsPanel({ c }: { c: Contact }) {
  const list = useConversations({ contact_id: c.id, status: "" });
  if (!list.data?.length) return null;
  return (
    <section className="card p-5" aria-label="Conversations">
      <h2 className="mb-2 font-semibold">Conversations</h2>
      <ul className="space-y-2 text-sm">
        {list.data.map((conv) => (
          <li key={conv.id}><Link to={`/inbox?c=${conv.id}`} className="text-brand hover:underline">{conv.channel.kind === "whatsapp" ? "WhatsApp" : "Email"}{conv.subject ? `: ${conv.subject}` : ""}</Link>
            <span className="block truncate text-xs text-muted">{conv.last_preview}</span></li>
        ))}
      </ul>
    </section>
  );
}

export function SequencesPanel({ c, perms }: { c: Contact; perms: string[] }) {
  const allowed = can(perms, "sequences:enrol");
  const sequences = useSequences(allowed);
  const enrolments = useContactEnrolments(c.id, allowed);
  const enrol = useEnrol();
  const stop = useStopEnrolment();
  const [choice, setChoice] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  if (!allowed) return null;
  const active = sequences.data?.filter((s) => s.is_active) ?? [];
  return (
    <section className="card space-y-3 p-5" aria-label="Sequences">
      <h2 className="font-semibold">Sequences</h2>
      {message && <Alert tone="warn">{message}</Alert>}
      {enrolments.data?.map((e) => (
        <div key={e.id} className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-medium">{e.sequence.name}</span>
          <Badge tone={e.status === "active" ? "brand" : e.status === "completed" ? "ok" : "neutral"}>{e.status}</Badge>
          <span className="flex-1 text-xs text-muted">{e.status === "active" ? `Step ${e.current_step + 1}${e.next_run_at ? `, ${formatDate(e.next_run_at)}` : ""}` : e.stop_reason}</span>
          {e.status === "active" && <button type="button" className="btn-ghost px-2 py-1 text-xs" onClick={() => void stop.mutateAsync(e.id)}>Stop</button>}
        </div>
      ))}
      {active.length > 0 ? (
        <div className="flex gap-2">
          <label htmlFor="enrol-seq" className="sr-only">Sequence</label>
          <select id="enrol-seq" className="input flex-1" value={choice} onChange={(e) => setChoice(e.target.value)}>
            <option value="">Choose a sequence…</option>{active.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <button type="button" className="btn-primary" disabled={!choice || enrol.isPending} onClick={() => {
            setMessage(null);
            void enrol.mutateAsync({ sequenceId: choice, contactIds: [c.id] }).then((r) => { if (r.skipped.length) setMessage(`Not enrolled: ${r.skipped[0]!.reason}`); setChoice(""); });
          }}>Enrol</button>
        </div>
      ) : <p className="text-sm text-muted">No active sequences. <Link to="/sequences" className="text-brand hover:underline">Create one</Link>.</p>}
    </section>
  );
}

export function PrivacyPanel({ c, perms }: { c: Contact; perms: string[] }) {
  const erase = useEraseContact();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState("");
  const [error, setError] = useState<string | null>(null);
  if (!can(perms, "privacy:manage")) return null;
  return (
    <section className="card space-y-3 p-5" aria-label="Privacy">
      <h2 className="font-semibold">Privacy requests</h2>
      <p className="text-xs text-muted">For access and erasure requests under GDPR, UK GDPR and India's DPDP Act.</p>
      <div className="flex flex-wrap gap-2">
        <button type="button" className="btn-ghost" onClick={() => void downloadContactExport(c.id, c.full_name)}>Download their data</button>
        <button type="button" className="btn-danger" onClick={() => setConfirming(true)}>Erase this person</button>
      </div>
      {confirming && (
        <Modal title={`Erase ${c.full_name}?`} onClose={() => setConfirming(false)}>
          <form className="space-y-4" onSubmit={(e) => {
            e.preventDefault(); setError(null);
            erase.mutateAsync({ id: c.id, confirm: typed }).then(() => navigate("/contacts", { replace: true }))
              .catch((err) => setError(err instanceof ApiError ? (err.problem.errors?.[0]?.message ?? err.message) : "Could not erase"));
          }}>
            <Alert tone="warn">Their name, email, phone, notes and messages are removed everywhere and cannot be recovered. Deals and quotes stay for your accounting records, without their personal details.</Alert>
            {error && <Alert>{error}</Alert>}
            <div><label htmlFor="erase-confirm" className="label">Type <b>{c.full_name}</b> to confirm</label><input id="erase-confirm" className="input" value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" /></div>
            <div className="flex justify-end gap-2"><button type="button" className="btn-ghost" onClick={() => setConfirming(false)}>Cancel</button>
              <button type="submit" className="btn-danger" disabled={erase.isPending || typed.trim().toLowerCase() !== c.full_name.toLowerCase()}>Erase permanently</button></div>
          </form>
        </Modal>
      )}
    </section>
  );
}
